import { sameLevels } from 'util/groups/approvalAssignments';
import { createAsyncThunk, createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { ListPaidolUserToProjectGroupsQuery, PaidolUser, ProjectGroup } from 'API';
import type { V2CreateProjectGroupInput, V2UpdateProjectGroupInput } from 'CDK_API';
import { RootState } from 'app/store/rootReducer';
// SEE-301 Phase F: create / update / remove go through the CDK API, so this slice no
// longer imports those project-group mutation documents. They stay in `graphql_custom`
// because other modules still import them.
//
// `updateProjectGroupAdmins` is deliberately NOT repointed here — SEE-1314 moved it to
// the CDK v2-user `adminSetGroupAdmins` mutation, which also carries approval levels and
// reviewers. `transactionService.v2UpdateProjectGroupAdmins` cannot express those, so
// repointing this thunk at it would silently drop that feature.
//
// SEE-1232: `GetProjectGroupsForPaidolId` is likewise gone from this import — the list read
// is now CDK-only. The document stays in `graphql_custom`, unreferenced: removing superseded
// Amplify documents is SEE-1238's job, and leaving it makes a revert a one-line transport flip.
// The same goes for `listPaidolUserToProjectGroups` (row e), now read through cardService.
import { cardService, GroupAdminLinksPage } from 'app/services/card.service';
import { userService } from 'app/services/user.service';
import {
  transactionService,
  type BasicHTTPResponse,
  type RemoveProjectGroupInput,
} from 'app/services/transaction.service';

// The link shape the Amplify read resolved; the CDK read serves the same fields.
type ProjectGroupAdminLink = NonNullable<
  NonNullable<ListPaidolUserToProjectGroupsQuery['listPaidolUserToProjectGroups']>['items'][number]
>;

const groupsEntityAdapter = createEntityAdapter<ProjectGroup>();

const initialState = groupsEntityAdapter.getInitialState({
  loading: false,
  editPending: false,
  nextToken: undefined as string | undefined,
  selectedGroupId: undefined as string | undefined,
  total: undefined as number | undefined,
});

export type ProjectGroupState = typeof initialState;

export interface GetGroupsArgs {
  paidolId: string;
  nextToken?: string;
  limit?: number;
  sort?: { field: string; direction: string };
  filter?: Record<string, unknown>;
}

export const selectProjectGroupsNextToken = (state: RootState) => state.cards?.projectGroups?.nextToken;

// 1) Fetch list of project groups
export const getProjectGroups = createAsyncThunk(
  'team/groups/getProjectGroups',
  async ({ paidolId, nextToken, limit, sort, filter }: GetGroupsArgs) => {
    // SEE-1232: the unsorted/unfiltered branch used to read Amplify's
    // listProjectGroupsByPaidolId; both branches now read the CDK field.
    const payload = await cardService.v2ListProjectGroupsByPaidolId({
      paidolId,
      nextToken,
      limit,
      sort,
      filter,
    });
    return {
      // Bridge CDK ProjectGroup items to the slice's API ProjectGroup (the create/update
      // paths stay on Amplify and are typed with the API type).
      items: (payload?.items ?? []) as unknown as ProjectGroup[],
      nextToken: payload?.nextToken ?? null,
      total: payload?.total,
    };
  }
);

// 2) Create a new project group
export const newProjectGroup = createAsyncThunk(
  'team/groups/newProjectGroup',
  async (input: V2CreateProjectGroupInput) => {
    return (await transactionService.v2CreateProjectGroup(input)) as unknown as ProjectGroup;
  }
);

// 3) Remove a project group
export const removeProjectGroup = createAsyncThunk(
  'team/groups/removeProjectGroup',
  async (input: RemoveProjectGroupInput): Promise<BasicHTTPResponse> => {
    return transactionService.removeProjectGroup(input);
  }
);

// 4) Update a project group
export const updateProject = createAsyncThunk(
  'team/groups/updateProjectGroup',
  async (input: V2UpdateProjectGroupInput) => {
    return (await transactionService.v2UpdateProjectGroup(input)) as unknown as ProjectGroup;
  }
);

type GroupAdminsInput = {
  projectGroupId: string;
  groupAdmins: Partial<PaidolUser>[];
  paidolId: string;
  /** Paidol user id -> the levels they sign off on. One person may hold both; absent means none. */
  approvalLevels?: Record<string, number[]>;
  /** Who may mark this group's transactions reviewed. Empty means every admin. */
  reviewerIds?: string[];
};

export const updateProjectGroupAdmins = createAsyncThunk(
  'team/groups/updateProjectGroupAdmins',
  async (input: GroupAdminsInput, { getState }) => {
    if (!input.projectGroupId || !input.paidolId) {
      throw new Error('projectGroupId and paidolId are required');
    }

    // Get current admins (handle pagination to get all records)
    const existingAdmins = [];
    let nextToken: string | null = null;

    do {
      // SEE-1232 row e: transport flip only — same page size, same pagination loop.
      const page: GroupAdminLinksPage<ProjectGroupAdminLink> =
        await cardService.listPaidolUserToProjectGroups({
          paidolId: input.paidolId,
          projectGroupId: input.projectGroupId,
          limit: 1000,
          nextToken,
        });
      existingAdmins.push(...page.items);
      nextToken = page.nextToken;
    } while (nextToken);
    const existingAdminIds = new Set(existingAdmins.map((admin) => admin?.paidolUserId).filter(Boolean));
    const newAdminIds = new Set(input.groupAdmins.map((admin) => admin.id).filter(Boolean));

    // Find admins to add (new ones)
    const adminsToAdd = input.groupAdmins.filter((admin) => admin.id && !existingAdminIds.has(admin.id));

    // Find admins to remove (existing ones not in new list OR duplicates)
    const seenUserIds = new Set<string>();
    const adminsToRemove = existingAdmins.filter((admin) => {
      if (!admin?.id || !admin?.paidolUserId) return false;

      // Remove if user is not in the new list
      if (!newAdminIds.has(admin.paidolUserId)) {
        return true;
      }

      // Remove if this is a duplicate (not the first occurrence)
      if (seenUserIds.has(admin.paidolUserId)) {
        return true;
      }

      seenUserIds.add(admin.paidolUserId);
      return false; // First occurrence, keep it
    });

    // Assignments ride along only when the editor showed the pickers — the
    // backend treats a present set as authoritative for every member.
    const sendAssignments = input.approvalLevels !== undefined || input.reviewerIds !== undefined;
    // A level change lands on a row that already exists, so it is neither an add
    // nor a remove and has to be diffed separately. Only a save carrying
    // assignments can change one, so a picker-less save keeps the short-circuit.
    const removedIds = new Set(adminsToRemove.map((admin) => admin?.id));
    const levelChanges = !sendAssignments
      ? []
      : existingAdmins.filter((admin) => {
          if (!admin?.id || !admin?.paidolUserId || removedIds.has(admin.id)) return false;
          return (
            !sameLevels(admin.approvalLevels, input.approvalLevels?.[admin.paidolUserId]) ||
            (admin.isReviewer ?? false) !== (input.reviewerIds ?? []).includes(admin.paidolUserId)
          );
        });

    // Only call the backend when something changes.
    if (adminsToAdd.length === 0 && adminsToRemove.length === 0 && levelChanges.length === 0) {
      const state = getState() as RootState;
      const currentGroup = selectProjectGroupById(state, input.projectGroupId);
      return currentGroup ?? null;
    }

    // SEE-1314: the join-table create/delete mutations are gone; the admin-only
    // backend mutation sets the full admin list (it diffs against the stored rows).
    // De-dupe: the mutation writes one row per id, and duplicate members
    // occur in real data (see the seenUserIds cleanup above).
    const paidolUserIds = [
      ...new Set(input.groupAdmins.map((admin) => admin.id).filter((id): id is string => Boolean(id))),
    ];
    await userService.adminSetGroupAdmins({
      paidolId: input.paidolId,
      groupType: 'PROJECT',
      groupId: input.projectGroupId,
      paidolUserIds,
      ...(sendAssignments && {
        assignments: paidolUserIds.map((id) => ({
          paidolUserId: id,
          approvalLevels: input.approvalLevels?.[id] ?? [],
          isReviewer: (input.reviewerIds ?? []).includes(id),
        })),
      }),
    });

    // Return updated list (handle pagination to get all records)
    const finalAdmins = [];
    let finalNextToken: string | null = null;

    do {
      const page: GroupAdminLinksPage<ProjectGroupAdminLink> =
        await cardService.listPaidolUserToProjectGroups({
          paidolId: input.paidolId,
          projectGroupId: input.projectGroupId,
          limit: 1000,
          nextToken: finalNextToken,
        });
      finalAdmins.push(...page.items);
      finalNextToken = page.nextToken;
    } while (finalNextToken);

    // Get current group from state and merge with updated admins
    const state = getState() as RootState;
    const currentGroup = selectProjectGroupById(state, input.projectGroupId);

    if (currentGroup) {
      return {
        ...currentGroup,
        groupAdmins: {
          __typename: 'ModelPaidolUserToProjectGroupConnection' as const,
          items: finalAdmins as any,
          nextToken: null,
        },
      };
    }

    return null;
  }
);

const groupsSlice = createSlice({
  name: 'team/groups',
  initialState,
  reducers: {
    resetProjectGroupsSlice: () => initialState,
    clearProjectGroups: (state) => {
      groupsEntityAdapter.removeAll(state);
      state.nextToken = undefined;
      state.total = undefined;
    },
    setSelectedProjectGroupId: (state, action) => {
      state.selectedGroupId = action.payload;
    },
    resetSelectedProjectGroupId: (state) => {
      state.selectedGroupId = undefined;
    },
    updateProjectGroupInState: (state, action: { payload: ProjectGroup }) => {
      if (action.payload) {
        groupsEntityAdapter.upsertOne(state, action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProjectGroups.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProjectGroups.fulfilled, (state, action) => {
      if (action.payload?.items) {
        groupsEntityAdapter.upsertMany(state, action.payload.items);
      }
      state.nextToken = action.payload?.nextToken ?? undefined;
      if (action.payload && 'total' in action.payload) {
        state.total = (action.payload as { total?: number }).total;
      }
      state.loading = false;
    });
    builder.addCase(getProjectGroups.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateProjectGroupAdmins.fulfilled, (state, action) => {
      if (action.payload) {
        groupsEntityAdapter.upsertOne(state, action.payload);
      }
      state.editPending = false;
    });
    builder.addCase(removeProjectGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeProjectGroup.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(removeProjectGroup.fulfilled, (state, action) => {
      // Extract projectGroupId from the original request
      const projectGroupId = (action.meta.arg as RemoveProjectGroupInput).projectGroupId;
      if (projectGroupId) {
        groupsEntityAdapter.removeOne(state, projectGroupId);
      }
      state.loading = false;
    });
    builder.addMatcher(
      isAnyOf(removeProjectGroup.pending, updateProject.pending, updateProjectGroupAdmins.pending),
      (state) => {
        state.editPending = true;
      }
    );
    builder.addMatcher(isAnyOf(newProjectGroup.fulfilled, updateProject.fulfilled), (state, action) => {
      if (action.payload) {
        groupsEntityAdapter.upsertOne(state, action.payload);
      }
      state.editPending = false;
    });

    builder.addMatcher(
      isAnyOf(removeProjectGroup.rejected, updateProject.rejected, updateProjectGroupAdmins.rejected),
      (state) => {
        state.editPending = false;
      }
    );
  },
});

export const {
  resetProjectGroupsSlice,
  clearProjectGroups,
  setSelectedProjectGroupId,
  resetSelectedProjectGroupId,
  updateProjectGroupInState,
} = groupsSlice.actions;

export const selectProjectGroupsSlice = (state: RootState) => state.cards?.projectGroups ?? initialState;

export const { selectAll: selectAllProjectGroups, selectById: selectProjectGroupById } =
  groupsEntityAdapter.getSelectors(selectProjectGroupsSlice);

export const selectSelectedProjectGroup = (state: RootState) => {
  if (state.cards?.projectGroups.selectedGroupId) {
    return selectProjectGroupById(state, state.cards?.projectGroups.selectedGroupId);
  }

  return undefined;
};

export default groupsSlice.reducer;
